import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Search, Download, Printer } from 'lucide-react';
import { exportToExcel } from '../utils/helpers';

export default function DataTable({
  columns, data = [], loading = false, totalItems = 0, page = 1, pageSize = 20,
  onPageChange, onSearch, title = 'Data', exportFilename = 'export', actions,
}) {
  const [searchVal, setSearchVal] = useState('');
  const totalPages = Math.ceil(totalItems / pageSize);

  const handleSearch = (e) => {
    setSearchVal(e.target.value);
    onSearch && onSearch(e.target.value);
  };

  const handleExcel = () => {
    const rows = data.map(row => {
      const obj = {};
      columns.forEach(col => { obj[col.label] = col.render ? String(col.render(row[col.key], row) || '') : (row[col.key] ?? ''); });
      return obj;
    });
    exportToExcel(rows, exportFilename);
  };

  const handlePrint = () => window.print();

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 p-3 border-b border-gray-100">
        <div className="relative flex-1 min-w-36">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={searchVal} onChange={handleSearch} placeholder="Search..."
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <div className="flex gap-2 ml-auto">
          <button onClick={handleExcel} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Download size={13}/> Excel
          </button>
          <button onClick={handlePrint} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Printer size={13}/> Print
          </button>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              {columns.map(col => (
                <th key={col.key} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {col.label}
                </th>
              ))}
              {actions && <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? (
              Array(5).fill(0).map((_, i) => (
                <tr key={i}>{columns.map(col => (
                  <td key={col.key} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td>
                ))}{actions && <td className="px-4 py-3"><div className="h-4 w-16 bg-gray-100 rounded animate-pulse mx-auto"/></td>}</tr>
              ))
            ) : data.length === 0 ? (
              <tr><td colSpan={columns.length + (actions ? 1 : 0)} className="text-center py-12 text-gray-400">No data found</td></tr>
            ) : (
              data.map((row, idx) => (
                <tr key={row._id || idx} className="hover:bg-gray-50 transition-colors">
                  {columns.map(col => (
                    <td key={col.key} className="px-4 py-3 text-gray-700">
                      {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '-')}
                    </td>
                  ))}
                  {actions && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">{actions(row)}</div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-gray-100">
        {loading ? (
          Array(4).fill(0).map((_, i) => <div key={i} className="p-4"><div className="h-20 bg-gray-100 rounded-lg animate-pulse"/></div>)
        ) : data.length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm">No data found</div>
        ) : (
          data.map((row, idx) => (
            <div key={row._id || idx} className="p-4 hover:bg-gray-50">
              {/* First 2 columns prominent, rest smaller */}
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1 min-w-0 mr-3">
                  {columns[0] && (
                    <p className="font-medium text-gray-900 truncate">
                      {columns[0].render ? columns[0].render(row[columns[0].key], row) : (row[columns[0].key] ?? '-')}
                    </p>
                  )}
                  {columns[1] && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {columns[1].render ? columns[1].render(row[columns[1].key], row) : (row[columns[1].key] ?? '-')}
                    </p>
                  )}
                </div>
                {columns[columns.length - 1] && (
                  <div className="flex-shrink-0">
                    {columns[columns.length - 1].render
                      ? columns[columns.length - 1].render(row[columns[columns.length - 1].key], row)
                      : (row[columns[columns.length - 1].key] ?? '-')
                    }
                  </div>
                )}
              </div>
              {/* Middle columns as small grid */}
              {columns.length > 3 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
                  {columns.slice(2, -1).map(col => (
                    <span key={col.key} className="text-xs text-gray-500">
                      <span className="text-gray-400">{col.label}: </span>
                      {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '-')}
                    </span>
                  ))}
                </div>
              )}
              {/* Actions */}
              {actions && (
                <div className="flex gap-2 pt-2 border-t border-gray-50">
                  {actions(row)}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-white">
          <p className="text-xs text-gray-500">
            {((page-1)*pageSize)+1}–{Math.min(page*pageSize, totalItems)} of {totalItems}
          </p>
          <div className="flex items-center gap-1">
            <button onClick={() => onPageChange(page-1)} disabled={page===1}
              className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              <ChevronLeft size={14}/>
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = page <= 3 ? i+1 : page-2+i;
              if (p < 1 || p > totalPages) return null;
              return (
                <button key={p} onClick={() => onPageChange(p)}
                  className={`min-w-8 h-8 rounded-lg text-xs font-medium ${p===page ? 'bg-blue-600 text-white' : 'border border-gray-200 hover:bg-gray-50 text-gray-700'}`}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => onPageChange(page+1)} disabled={page===totalPages}
              className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              <ChevronRight size={14}/>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
